import { ternaryHandler, sanitizeObject, updateDateFormat, accountAgeCalculator } from '../datalayerHelpers';

/**
 * Extracts and maps metadata properties from the provided datalayer object.
 *
 * @param datalayer - A record containing key-value pairs representing the current datalayer state.
 * @returns A sanitized object containing the mapped metadata values (e.g., site info, geolocation, platform).
 */
export const getMetadata = (datalayer: Record<string, unknown>) => {
  return sanitizeObject({
    allow_marketing: true, //always true if ACDL enabled
    site_id: ternaryHandler(datalayer?.['env_site_id']),
    language_domain: ternaryHandler(datalayer?.['env_site_id']),
    platform: ternaryHandler(datalayer?.['env_platform']),
    server_id: ternaryHandler(datalayer?.['env_server_id']),
    session_id: ternaryHandler(datalayer?.['sessionId']),
    latitude: ternaryHandler(datalayer?.['browser_akamai_loc_lat']),
    longitude: ternaryHandler(datalayer?.['browser_akamai_loc_long']),
    geo_country: ternaryHandler(datalayer?.['browser_akamai_loc_country']),
    geo_state: ternaryHandler(datalayer?.['browser_akamai_loc_state']),
    geo_city: ternaryHandler(datalayer?.['browser_akamai_loc_city']),
  });
};

/**
 * Constructs page-specific analytics data based on whether the context is a modal or a full page.
 *
 * @param options - An object containing properties to determine page context.
 * @param options.isModel - A boolean indicating if the current view is a modal (note: property name is `isModel`).
 * @param options.modelName - The name of the modal, used if `isModel` is true.
 * @returns A sanitized object containing the page or modal name and a modal flag.
 */
export const getPageData = ({ isModel, modelName }: { isModel: boolean; modelName: string }) => {
  let windowUrl = '';
  let completeURL = '';
  if (typeof window !== 'undefined') {
    const { host, pathname, hash, href } = window.location;
    windowUrl = `${host}${pathname}${hash}`;
    completeURL = href.replace(/^https?:\/\//, '');
  }

  return sanitizeObject({
    name: isModel ? modelName : windowUrl,
    url: completeURL,
    modal: !!isModel,
  });
};

/**
 * Extracts and maps user-specific properties from the provided datalayer object.
 *
 * @param datalayer - A record containing key-value pairs representing the current datalayer state.
 * @returns A sanitized object containing the mapped user data values (e.g., authentication status, member details, points balance).
 */
export const getUserData = (datalayer: Record<string, unknown>) => {
  return sanitizeObject({
    alt_customer_id: ternaryHandler(datalayer?.['altCustId'], ternaryHandler(datalayer?.['mr_id_alternate'])),
    authentication_status: ternaryHandler(datalayer?.['memState']),
    member_level: ternaryHandler(datalayer?.['mr_prof_member_program_level']),
    member_city: ternaryHandler(datalayer?.['mr_prof_address_city']),
    member_state: ternaryHandler(datalayer?.['mr_prof_address_state']),
    member_country: ternaryHandler(datalayer?.['mr_prof_address_country']),
    upcoming_stay: ternaryHandler(datalayer?.['mr_prof_upcoming_stay']),
    stay_in_progress: ternaryHandler(datalayer?.['mr_prof_stay_in_progress']),
    cc_all: ternaryHandler(datalayer?.['mr_prof_cc_all']),
    nights_booked_this_year: ternaryHandler(datalayer?.['mr_prof_nights_booked_this_year']),
    points_balance: ternaryHandler(datalayer?.['mr_prof_points_balance']),
    member_join_date: updateDateFormat(ternaryHandler(datalayer?.['mr_prof_join_date']) as string),
    nights_to_renew_current_member_level: ternaryHandler(datalayer['mr_prof_nights_to_renew_current']),
    account_age: accountAgeCalculator(datalayer ?? {}),
  });
};
